import reject from "lodash/reject";
import map from "lodash/map";
import React from "react";
import type { AnyAction } from "redux";
import EditableSetup, { AppMetadata, Tool, ToolPayload } from "./EditableSetup";
import EditableToolEdit from "./EditableToolEdit";
import AppModal from "src/shared/components/AppModal";
import AppPage from "src/shared/components/AppPage";
import logEvent from "src/utils/logEvent";

export type EditableToolProps = {
  appMetadata: AppMetadata;
  classId: string;
  mountPoint: string;
  toolType: string;
  title?: string;

  tools: Tool[];
  suggestedTools?: Tool[];

  displayOpen: (path: string, displayPayload?: AnyAction) => void;
  displayClose: VoidFunction;
  remoteClose: VoidFunction;
  createTool: (data: { classId: string; type: string; payload: ToolPayload; dojoId?: string }) => void;
  editTool: (data: { classId: string; _id: string; payload: ToolPayload; updatedAt: string }) => void;
  deleteTool: (data: { _id: string; classId: string }) => void;

  getDisplayPayload: (tool: Tool) => AnyAction;

  renderEditItem: ({
    onEdit,
    payload,
  }: {
    onEdit: (payload: ToolPayload, disabled?: boolean) => void;
    payload: ToolPayload;
  }) => React.ReactElement;
  renderItem: ({
    tool,
    openEditScreen,
    showTool,
  }: {
    tool: Tool;
    openEditScreen: (tool: Tool) => void;
    showTool: (tool: Tool) => void;
  }) => React.ReactElement;
  renderResult: ({ tool }: { tool: Tool }) => React.ReactElement;
};

type EditableToolState = {
  selectedTool: Tool | null;
  editScreen: boolean;
};

// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
export default class EditableTool extends React.Component<EditableToolProps, EditableToolState> {
  constructor(props: EditableToolProps) {
    super(props);
    this.state = {
      editScreen: false,
      selectedTool: null,
    };
  }

  componentDidMount() {
    logEvent({
      eventName: `${this.props.toolType}.setup.open`,
    });
  }

  componentWillUnmount() {
    logEvent({
      eventName: `${this.props.toolType}.setup.close`,
    });
  }

  render() {
    const { displayClose, displayOpen, remoteClose, mountPoint, classId } = this.props;
    let { tools, suggestedTools } = this.props;
    if (tools && suggestedTools) {
      suggestedTools = reject(suggestedTools, ({ dojoId }) => map(tools, "dojoId").includes(dojoId));
      tools = tools.concat(suggestedTools);
    }

    const { selectedTool, editScreen } = this.state;

    if (editScreen) {
      return (
        <EditableToolEdit
          renderEditItem={this.props.renderEditItem}
          closeEditScreen={this.closeEditScreen}
          appMetadata={this.props.appMetadata}
          createTool={this.createTool}
          editTool={this.editTool}
          deleteTool={this.deleteTool}
          tool={this.state.selectedTool}
          showTool={this.showTool}
        />
      );
    } else if (selectedTool) {
      return (
        <AppModal
          {...this.props.appMetadata}
          onRequestHide={() => {
            displayClose();
            this.setState({ selectedTool: null });
            remoteClose();
          }}
          onRequestStart={
            mountPoint === "mobile" ? () => displayOpen(`/apps/display/${this.props.toolType}`) : undefined
          }
          onRequestStop={mountPoint === "mobile" ? () => this.props.displayClose() : undefined}
        >
          {this.props.renderResult({ tool: selectedTool })}
        </AppModal>
      );
    }

    return (
      <AppPage {...this.props.appMetadata} classId={classId} mountPoint={mountPoint}>
        <EditableSetup
          renderItem={this.props.renderItem}
          openEditScreen={this.openEditScreen}
          tools={tools}
          title={this.props.title}
          showTool={this.showTool}
          toolType={this.props.toolType}
          appMetadata={this.props.appMetadata}
        />
      </AppPage>
    );
  }

  createTool = (payload: Record<string, unknown>, dojoId?: string) => {
    this.props.createTool({
      classId: this.props.classId,
      type: this.props.toolType,
      payload,
      dojoId,
    });
  };

  editTool = (_id: string, payload: Record<string, unknown>) => {
    this.props.editTool({
      classId: this.props.classId,
      _id,
      payload,
      updatedAt: new Date().toISOString(),
    });
  };

  deleteTool = (_id: string) => {
    this.props.deleteTool({
      _id,
      classId: this.props.classId,
    });
    this.setState({
      editScreen: false,
      selectedTool: null,
    });
  };

  updateLastUsed(tool: Tool) {
    const { _id, dojoId, payload } = tool;

    if (_id) {
      this.editTool(_id, payload);
    } else if (dojoId) {
      this.createTool(payload, dojoId);
    }
  }

  showTool = (tool: Tool) => {
    this.setState({
      editScreen: false,
      selectedTool: tool,
    });

    this.props.displayOpen(`/apps/display/${this.props.toolType}`, this.props.getDisplayPayload(tool));
    this.updateLastUsed(tool);
  };

  openEditScreen = (tool: Tool | null = null) => {
    this.setState({
      editScreen: true,
      selectedTool: tool,
    });
  };

  closeEditScreen = () => {
    return this.setState({
      editScreen: false,
      selectedTool: null,
    });
  };
}
